/*Copyright (c) 2023. Honeywell International, Inc. All rights reserved.
This file contains trade secrets of Honeywell International, Inc. 
No part may be reproduced or transmitted in any form by any means or for any purpose without the express written permission of Honeywell. */
import { createSlice } from "@reduxjs/toolkit";
import UserState from "../../models/storeModels/UserState";

import {
  deleteTenantUser,
  editUser,
  exportUserListBasedOnForgeCustomerId,
  getUserExportListBasedOnForgeCustomerId,
  getUserListBasedOnForgeCustomerId,
  getOrganizationUserByTenantID,
  getUserPermissions,
  getUserRole,
} from "./user-actions";
import UserPermissions from "../../models/apiModels/UserPermissions";
import { WrapperUserPermissions } from "../../models";
import Personas from "../../models/componentModels/Personas";

interface UserRolePayload {
  roleId: string;
  name: string;
}

let users = [
  {
    userId: "user0",
    firstName: "FirstName0",
    lastName: "LastName0",
    emailId: "user0@example.com",
    roles: [
      { roleId: "1", name: "Admin" },
      { roleId: "2", name: "User" },
    ],
    phoneNumber: "1234567890",
    organization: [{ id: "org0", name: "OrgName0" }],
    customerId: "customer0",
    sso_enabled: true,
  },
  {
    userId: "user1",
    firstName: "FirstName1",
    lastName: "LastName1",
    emailId: "user1@example.com",
    roles: [],
    phoneNumber: "1234567891",
    organization: [{ id: "org1", name: "OrgName1" }],
    customerId: "customer1",
    sso_enabled: true,
  },
  {
    userId: "user2",
    firstName: "FirstName2",
    lastName: "LastName2",
    emailId: "user2@example.com",
    roles: [
      { roleId: "3", name: "Guest" },
      { roleId: "4", name: "Member" },
    ],
    phoneNumber: "1234567892",
    organization: [{ id: "org2", name: "OrgName2" }],
    customerId: "customer2",
    sso_enabled: true,
  },
  {
    userId: "user3",
    firstName: "FirstName3",
    lastName: "LastName3",
    emailId: "user3@example.com",
    roles: [],
    phoneNumber: "1234567893",
    organization: [{ id: "org3", name: "OrgName3" }],
    customerId: "customer3",
    sso_enabled: true,
  },
  {
    userId: "user4",
    firstName: "FirstName4",
    lastName: "LastName4",
    emailId: "user4@example.com",
    roles: [{ roleId: "5", name: "Owner" }],
    phoneNumber: "1234567894",
    organization: [{ id: "org4", name: "OrgName4" }],
    customerId: "customer4",
    sso_enabled: true,
  },
  {
    userId: "user5",
    firstName: "FirstName5",
    lastName: "LastName5",
    emailId: "user5@example.com",
    roles: [
      { roleId: "6", name: "Contributor" },
      { roleId: "1", name: "Admin" },
    ],
    phoneNumber: "1234567895",
    organization: [{ id: "org5", name: "OrgName5" }],
    customerId: "customer5",
    sso_enabled: true,
  },
  {
    userId: "user6",
    firstName: "FirstName6",
    lastName: "LastName6",
    emailId: "user6@example.com",
    roles: [],
    phoneNumber: "1234567896",
    organization: [{ id: "org6", name: "OrgName6" }],
    customerId: "customer6",
    sso_enabled: true,
  },
];

const initialSliceState: UserState = {
  users: [],
  tenantUsersafterPersonaFilter: [],
  originalGetUserData: [],
  isLoadingIndicatorActive: false,
  errorCode: null,
  hasError: false,
  exportedData: [],
  filteredUsers: [],
  rolestoFilterTenants: [],
  selectedRolesFilter: [],
  FilterApplied: false,
  userPermissions: {
    can_create_organisation: false,
    can_create_organisation_user: false,
    can_create_tenant: false,
    can_create_user: false,
    can_read_organisation_logo: false,
    can_delete_organisation: false,
    can_delete_organisation_user: false,
    can_delete_tenant: false,
    can_delete_user: false,
    can_read_all_roles: false,
    can_read_organisation: false,
    can_read_organisation_user: false,
    can_read_tenant: false,
    can_read_tenant_logo: false,
    can_read_user: false,
    can_read_user_logo: false,
    can_update_organisation: false,
    can_update_organisation_user: false,
    can_update_tenant: false,
    can_update_user: false,
    is_active_user: false,
    can_read_tenant_settings: false,
    can_update_tenant_settings: false,
    can_update_tenant_product: false,
    user_persona: Personas.NoAccess,
  },
  isUserPermissionsFetched: false,
  exportedAdminUsers: [],
  tokenData: {
    customer: "",
    tenant: "",
    userId: "",
  },
};

const userSlice = createSlice({
  name: "user",
  initialState: initialSliceState,

  extraReducers(builder) {
    // get users of a customer
    builder
      .addCase(
        getUserListBasedOnForgeCustomerId.fulfilled,
        (state, action: any) => {
          state.users = action.payload;
          state.isLoadingIndicatorActive = false;
        }
      )
      .addCase(getUserListBasedOnForgeCustomerId.rejected, (state, action) => {
        state.isLoadingIndicatorActive = false;
        state.hasError = true;
        state.errorCode = setErrorCode(action);
      })
      .addCase(getUserListBasedOnForgeCustomerId.pending, (state, action) => {
        // Check if the action was called with silent flag
        const payload = action.meta.arg;
        const silent = typeof payload === "object" ? payload.silent : false;

        // Only set loading state if not silent
        if (!silent) {
          state.isLoadingIndicatorActive = true;
        }
      });
    builder
      .addCase(
        getOrganizationUserByTenantID.fulfilled,
        (state, action: any) => {
          let data = [...action.payload.originalUserData, ...state.users];
          data = Array.from(
            new Set(
              data
                .map(({ ...rest }) => rest)
                .map((obj: any) => JSON.stringify(obj))
            )
          ).map((strObj) => JSON.parse(strObj));
          data = data.map((ele) => {
            return { ...ele };
          });
          state.users = data;
          state.originalGetUserData = action.payload.originalUserData;
        }
      )
      .addCase(getOrganizationUserByTenantID.rejected, (state, action) => {})
      .addCase(getOrganizationUserByTenantID.pending, (state) => {});

    // get User Exported List
    builder
      .addCase(
        exportUserListBasedOnForgeCustomerId.fulfilled,
        (state, action: any) => {
          state.exportedData = action.payload;
          state.isLoadingIndicatorActive = false;
        }
      )
      .addCase(
        exportUserListBasedOnForgeCustomerId.rejected,
        (state, action) => {
          state.isLoadingIndicatorActive = false;
          state.hasError = true;
          state.errorCode = setErrorCode(action);
        }
      )
      .addCase(exportUserListBasedOnForgeCustomerId.pending, (state) => {
        state.isLoadingIndicatorActive = true;
        // state.changed = true;
      });

    // delete users of a  customer
    builder
      .addCase(deleteTenantUser.fulfilled, (state, action) => {
        state.users = state.users.filter(
          (x) => !action.payload.userIds.some((y) => y === x.userId)
        );
        state.isLoadingIndicatorActive = false;
      })
      .addCase(deleteTenantUser.rejected, (state, action) => {
        state.isLoadingIndicatorActive = false;
        state.hasError = true;
        state.errorCode = setErrorCode(action);
      })
      .addCase(deleteTenantUser.pending, (state) => {
        state.isLoadingIndicatorActive = true;
      });

    // get users permissions
    builder
      .addCase(getUserPermissions.fulfilled, (state, action: any) => {
        // Keep the persona set by getUserRole; PopulateUserPermissions resets it to Personas.NoAccess.
        const existingPersona = state.userPermissions.user_persona;
        state.userPermissions = PopulateUserPermissions(action.payload.result);
        state.userPermissions.user_persona = existingPersona;
        state.isUserPermissionsFetched = true;
        state.isLoadingIndicatorActive = false;
      })
      .addCase(getUserPermissions.rejected, (state, action) => {
        state.isLoadingIndicatorActive = false;
        state.hasError = true;
        state.errorCode = setErrorCode(action);
      })
      .addCase(getUserPermissions.pending, (state) => {
        state.isLoadingIndicatorActive = true;
      });

    // store user role/persona in the redux store
    builder
      .addCase(
        getUserRole.fulfilled,
        (state, action: { payload: UserRolePayload }) => {
          state.userPermissions.user_persona = action.payload.name as Personas;
        }
      )
      .addCase(getUserRole.rejected, (state, action) => {
        state.hasError = true;
        state.errorCode = setErrorCode(action);
      })
      .addCase(getUserRole.pending, (state) => {
        state.isLoadingIndicatorActive = true;
      });

    // get Admin Users export list

    builder
      .addCase(
        getUserExportListBasedOnForgeCustomerId.fulfilled,
        (state, action: any) => {
          state.exportedAdminUsers = action.payload;
        }
      )
      .addCase(
        getUserExportListBasedOnForgeCustomerId.rejected,
        (state, action) => {}
      )
      .addCase(getUserExportListBasedOnForgeCustomerId.pending, (state) => {})
      // add case for fulfilled Tenant users on Edit
      .addCase(editUser.fulfilled, (state, action: any) => {
        // Find the user by userId
        const stateUsers = [...state.users];
        const user = stateUsers.find((x) => x.userId === action.payload.userId);

        // Update the user List
        if (user) {
          user.firstName = action.payload.firstName;
          user.lastName = action.payload.lastName;
          user.emailId = action.payload.emailId;
          user.roles = action.payload.responseRoles;
          user.phoneNumber = action.payload.phoneNumber;
          user.organization = action.payload.responseOrgs;
          state.users = [...stateUsers];
        }

        /////////
        // const tenanttoUpdate = state.tenants.find(
        //   (x) => x.id === action.payload.id
        // );
        // Object.assign(tenanttoUpdate as Tenant, action.payload as Tenant);
        // state.tenants = [...state.tenants];
        ////////
      });
  },
  reducers: {
    setOrgUserListEmpty(state, action) {
      state.originalGetUserData = action.payload;
    },
    showRoleList(state, action) {
      state.rolestoFilterTenants = action.payload;
    },
    filteredRoleList(state, action) {
      state.selectedRolesFilter = action.payload.selectedValue;
      state.FilterApplied = action.payload.filterApplied;
    },
    setFilteredUserList(state, action) {
      state.filteredUsers = action.payload;
    },

    resetUserListBasedOnForgeCustomerId(state) {
      state.users = [];
    },

    tenantUsersafterPersonaFilterforFilterCount(state, action) {
      state.tenantUsersafterPersonaFilter = action.payload;
    },

    // Update seelcted user roles
    // updateUserRoles(state, action) {
    //   const userToUpdate = state.users.find(
    //     (x) => x.userId === action.payload.userId
    //   );
    //   const newRoles = state.rolestoFilterTenants
    //     .filter((x) => action.payload.roleIds.includes(x.role.roleId))
    //     .map((x) => x.role);

    //   // Update user roles if userToUpdate is present
    //   if (userToUpdate !== undefined) {
    //     userToUpdate.roles = newRoles;
    //     state.users = [...state.users];
    //   }
    // },
  },
});

function PopulateUserPermissions(userPermissions: UserPermissions) {
  const permissionsWrapper: WrapperUserPermissions = {
    can_create_organisation: userPermissions.can_create_organisation,
    can_create_organisation_user: userPermissions.can_create_organisation_user,
    can_create_tenant: userPermissions.can_create_tenant,
    can_create_user: userPermissions.can_create_user,
    can_read_organisation_logo: userPermissions.can_read_organisation_logo,
    can_delete_organisation: userPermissions.can_delete_organisation,
    can_delete_organisation_user: userPermissions.can_delete_organisation_user,
    can_delete_tenant: userPermissions.can_delete_tenant,
    can_delete_user: userPermissions.can_delete_user,
    can_read_all_roles: userPermissions.can_read_all_roles,
    can_read_organisation: userPermissions.can_read_organisation,
    can_read_organisation_user: userPermissions.can_read_organisation_user,
    can_read_tenant:
      userPermissions.can_read_tenant || userPermissions.can_read_all_tenant,
    can_read_tenant_logo: userPermissions.can_read_tenant_logo,
    can_read_user:
      userPermissions.can_read_user || userPermissions.can_read_all_users,
    can_read_user_logo: userPermissions.can_read_user_logo,

    can_update_organisation: userPermissions.can_update_organisation,
    can_update_organisation_user: userPermissions.can_update_organisation_user,
    can_update_tenant:
      userPermissions.can_update_tenant ||
      userPermissions.can_update_tenant_basic,
    can_update_user: userPermissions.can_update_user,
    is_active_user: userPermissions.is_active_user,
    can_read_tenant_settings: userPermissions.can_read_tenant_settings,
    can_update_tenant_settings: userPermissions.can_update_tenant_settings,
    can_update_tenant_product: userPermissions.can_update_tenant,
    user_persona: Personas.NoAccess,
  };
  return permissionsWrapper;
}

function setErrorCode(action: any) {
  let errorCode = action.payload as number;
  return errorCode;
}

export const userActions = userSlice.actions;

export default userSlice;
